import {
  ErrorComponent,
  FormFieldComponent,
  NumericTextBoxComponent, PrefixTemplateDirective,
  TextAreaComponent,
  TextBoxComponent
} from '@progress/kendo-angular-inputs';
import { OnInit, Component, inject, Input, OnDestroy, computed } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ComboBoxComponent } from '@progress/kendo-angular-dropdowns';
import { ControlNamePipe } from 'src/app/pipes/control-name.pipe';
import { CustomMessagesComponent, LabelComponent } from '@progress/kendo-angular-label';
import { TranslatePipe } from '@ngx-translate/core';
import { LowerCasePipe, NgClass } from '@angular/common';
import { SVGIconComponent } from '@progress/kendo-angular-icons';
import { Icons } from 'src/app/icons';
import { TestDataService } from 'src/app/services/testData.service';
import { Subscription } from 'rxjs';
import { mockData } from 'src/app/mock/data';
import { Utils } from 'src/app/utils';
import { FormService } from 'src/app/services/form.service';
import { SpacerComponent } from 'src/app/ui/atoms/spacer/spacer.component';

@Component({
    selector: 'app-renter-info',
    imports: [
        TextBoxComponent,
        FormsModule,
        ErrorComponent,
        ComboBoxComponent,
        LabelComponent,
        TextAreaComponent,
        ReactiveFormsModule,
        FormFieldComponent,
        ControlNamePipe,
        NumericTextBoxComponent,
        TranslatePipe,
        NgClass,
        CustomMessagesComponent,
        PrefixTemplateDirective,
        SVGIconComponent,
        LowerCasePipe,
        SpacerComponent
    ],
    templateUrl: './renter-info.component.html',
    styleUrl: './renter-info.component.scss'
})
export class RenterInfoComponent implements OnInit, OnDestroy {
  private testDataService = inject(TestDataService);
  private testDataSubscription: Subscription;

  private formService = inject(FormService);

  readonly icons = Icons;

  @Input({required: true}) formId: string;

  formGroup: FormGroup;

  private dialCodes = computed(() => {
    return this.formService.countries().map((c: any, i: number) => ({
      ...c,
      id: i,
      label: `${c.dial_code} (${c.code})`
    }));
  });

  filteredDialCodes = this.dialCodes();

  ngOnInit() {
    const defaultDialCode = this.formService.params['dialCode'];
    const dialCode = this.formService.form()?.dialCode
      || this.dialCodes().find((d: any) => d.code === defaultDialCode);

    this.formGroup = new FormGroup({
      renterName: new FormControl(this.formService.form().renterName, [Validators.required, Validators.minLength(2)]),
      email: new FormControl(this.formService.form().email, [
        Validators.required, Validators.minLength(2), Validators.email],
      ),
      dialCode: new FormControl(dialCode, [Validators.required, Validators.minLength(2)]),
      number: new FormControl(this.formService.form().phone, [Validators.required, Validators.minLength(1)]),
      address: new FormControl(this.formService.form().address, [Validators.required, Validators.minLength(1)]),
      comment: new FormControl(this.formService.form().comment)
    });

    this.formGroup.valueChanges.subscribe(() => this.buildRenterInfoData());
    this.initTestDataObserver();
  }

  private initTestDataObserver() {
    this.testDataSubscription = this.testDataService.fillDataObserver.subscribe(() => {
      if (document.getElementById(this.formId)) {
        const renterName = Utils.getRandomElement(mockData.names) + ' ' + Utils.getRandomElement(mockData.lastNames);
        this.formGroup.get('renterName')?.setValue(renterName);
        this.formGroup.get('number')?.setValue(0);
        this.formGroup.get('address')?.setValue(Utils.getRandomElement(mockData.addresses));
        this.formGroup.markAllAsTouched();
      }
    });
  }

  filterDialCodes(value: any) {
    if (!value || value.length < 2) {
      this.filteredDialCodes = this.dialCodes();
      return;
    }
    this.filteredDialCodes = this.dialCodes().filter((d: any) => {
      const v = value?.toLowerCase();
      return d.label.toLowerCase().includes(v) || d.name.toLowerCase().includes(v);
    });
  }

  private buildRenterInfoData() {
    const values: any = this.formGroup.getRawValue();
    this.formService.update({
      ...this.formService.form(),
      renterName: values.renterName,
      email: values.email,
      dialCode: values.dialCode,
      phone: values.number,
      address: values.address,
      comment: values.comment,
    });
  }

  submit() {
    this.formGroup.markAllAsTouched();
    this.buildRenterInfoData();
  }

  ngOnDestroy() {
    this.testDataSubscription?.unsubscribe();
  }
}
